import {
  Button, GooglePlacesInput, Input, Label,
} from '@components/common/Forms';
import { Trans, useTranslation } from 'next-i18next';
import {
  useCallback, useEffect, useState,
} from 'react';
import { useExtendedSearchParams } from '@hooks/next-routing-wrappers';
import { fetcher, isApiV1Errors } from 'src/utils';
import poweredByStripe from '@images/svg/payment/powered_by_stripe.svg';
import { CreditCardList } from '@components/features/payment/cards/CreditCardList';
import { bjToast, ImageBj } from '@components/common';
import {
  ApiUserSubscriptionOrder,
  ApiUserSubscriptionPaymentDetails, ApiUserSubscriptionPaymentDetailsOk,
} from '@type/v1-api-types/user-subscription';
import {
  isPaymentDetailsStatusOk,
  isUserSubscriptionOrderOk,
} from 'src/type-predicates/v1-api-predicates/user-subscription';
import { useBjForm } from '@hooks/useBjForm';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { PencilIcon } from '@heroicons/react/24/solid';

// Interface
// *****************************************************
interface TalentSubscriptionHubModalProps {
  locale: Locale,
  paymentDetailsServer?: ApiUserSubscriptionPaymentDetailsOk,
}


/**
 * @description Description
 */
export const TalentSubscriptionHubModal = (props: TalentSubscriptionHubModalProps) => {
  // Destructure props
  const {
    locale,
    paymentDetailsServer,
  } = props;

  const { searchParams } = useExtendedSearchParams();
  const [paymentDetails, setPaymentDetails] = useState<ApiUserSubscriptionPaymentDetailsOk | undefined>(paymentDetailsServer);

  const { t } = useTranslation('common');
  const {
    record, setFormValues, handleSubmit, formStatus,
  } = useBjForm({
    submitURL: '/v1/user-subscription/order',
  });

  const [paymentFormVisible, setPaymentFormVisible] = useState(false);

  //  populate data based
  useEffect(() => {
    if (paymentDetails) {
      if (paymentDetails?.firstName === null || paymentDetails?.lastName === null || paymentDetails?.address === null) {
        setFormValues({
          email: paymentDetails.email,
          price: paymentDetails.price,
          packageId: paymentDetails.packageId,
        });
        setPaymentFormVisible(true);
      } else {
        setFormValues(paymentDetails);
      }
    }
  }, [paymentDetails, setFormValues]);

  const fetchPaymentDetails = useCallback(() => {
    const selectedProduct = searchParams?.get('selectedProduct');
    fetcher<ApiUserSubscriptionPaymentDetails>(`/v1/user-subscription/payment-details${selectedProduct ? `?packageId=${selectedProduct}` : ''}`, locale, {
      method: 'GET',
    })
      .then((response) => {
        if (isPaymentDetailsStatusOk(response)) {
          setPaymentDetails(response);
        }

        // get error message from user subscription
        if (isApiV1Errors(response)) {
          bjToast.error(response.errors[0].message);

          return null;
        }

        return null;
      })
      .catch(() => {
      });
  }, [locale, searchParams]);

  useEffect(() => {
    fetchPaymentDetails();
  }, [fetchPaymentDetails]);

  // handles form submission response
  useEffect(() => {
    // form submitted successfully
    if (formStatus.actionStatus === 'success') {
      fetchPaymentDetails();
      bjToast.success(t('global.action.save_successful'));

      const successData = formStatus.data as ApiUserSubscriptionOrder;
      if (isUserSubscriptionOrderOk(successData) && successData !== undefined) {
        window.location.replace(successData.checkoutSessionUrl);
      }
    }

    // form submitted with error
    if (formStatus.actionStatus === 'error' && isApiV1Errors(formStatus.data)) {
      bjToast.error(formStatus.data.errors[0].message);
    }
  }, [formStatus, fetchPaymentDetails, t]);

  const currentDate = new Date();
  const expireDate = new Date();
  expireDate.setDate(currentDate.getDate() + 30);

  const benefits = [
    { id: 1, title: t('premium.talent.trial.faqs.1.question'), text: t('premium.talent.trial.faqs.1.answer') },
    { id: 2, title: t('premium.talent.trial.faqs.2.question'), text: t('premium.talent.trial.faqs.2.answer') },
    { id: 3, title: t('premium.talent.trial.faqs.3.question'), text: t('premium.talent.trial.faqs.3.answer') },
  ];

  const subscriptionPrice = `€ ${paymentDetails?.recurrentPrice.toFixed(2)}`;
  const discountAmount = `€ ${paymentDetails?.priceDiscount.toFixed(2)}`;
  const totalPrice = `€ ${paymentDetails?.price.toFixed(2)}`;
  const afterTrialPrice = `€ ${((paymentDetails?.recurrentPrice || 0) - (paymentDetails?.priceDiscount || 0)).toFixed(2)}`;


  // Render component
  // *****************************************
  return (
    <div className="container relative min-h-96 bg-surface">
      <div className="my-6 rounded-md border-2 border-solid py-6">

        {/* Subscription name and base price */}
        <div className="-mt-1 flex items-center justify-between gap-6 px-6 pb-2 text-lg font-bold">
          <h2>{t('premium.talent.hero.heading')}</h2>
          <h3 className="text-secondary">{subscriptionPrice}</h3>
        </div>

        {/* Trial description */
          paymentDetails?.price === 0 && (
            <div className="my-3 bg-surface-100 p-6">
              <Trans
                i18nKey="premium.talent.trial.desc"
                values={{
                  startDate: currentDate.toLocaleDateString('en-GB'),
                  endDate: expireDate.toLocaleDateString('en-GB'),
                }}
              />
            </div>
          )
        }

        {/* Neuro discount */}
        <div className="mb-6 flex flex-wrap border-b px-6 pb-6 pt-3">
          <div className="w-1/2 text-md">
            {t('subscription.neuro.discount.label')}
          </div>
          <div className="w-1/2 text-right text-md font-bold text-secondary">
            {discountAmount}
          </div>
        </div>

        { /* After trial price */
          paymentDetails?.price === 0 && (
            <div className="-mt-6 mb-6 flex flex-wrap border-b p-6">
              <div className="w-1/2 text-md">
                {t('premium.talent.trial_exp.price')}
              </div>
              <div className="w-1/2 text-right text-md font-bold text-secondary">
                {afterTrialPrice}
              </div>
            </div>
          )
        }

        <div className="flex flex-wrap px-6">
          <div className="w-1/2 text-md">
            {t('global.label.total_this_month')}
          </div>
          <div className="w-1/2 text-right text-md font-bold text-secondary">
            {totalPrice}
          </div>
        </div>
      </div>


      <hr className="my-6" />

      <div className="text-sm font-bold uppercase text-ink-light">{t('page.company.billing.information')}</div>

      {
        !paymentFormVisible && (
          <div className="my-6 flex justify-between rounded-md border-2 border-solid p-6">
            <div>
              <strong>{`${paymentDetails?.firstName} ${paymentDetails?.lastName}`}</strong>
              <p>{paymentDetails?.address}</p>
            </div>
            <Button
              styling="none"
              onClick={() => setPaymentFormVisible(true)}
            >
              <PencilIcon className="size-6 text-primary" />
            </Button>
          </div>
        )
      }

      <form onSubmit={handleSubmit} className="mb-8" noValidate>
        {/* HIDDEN INPUTS with the extra fields needed for the 'salary' nested object  */}
        <Input type="hidden" {...record('price')} />
        <Input type="hidden" {...record('packageId')} />


        <div className={!paymentFormVisible ? 'hidden' : 'grid grid-cols-1 md:grid-cols-6 md:gap-x-4'}>
          {paymentDetails?.email && paymentDetails.email !== '' ? (
            <Input type="hidden" {...record('email')} />
          ) : (
            <Label
              className="col-span-6"
              description={t('premium.talent.payment.email')}
              descriptionClass="font-bold !mb-2 text-ink-medium text-sm"
              required
            >
              <Input
                type="text"
                {...record('email')}
                placeholder={t('premium.talent.placeholder.email')}
                minLength={1}
                required
                customValidityMessages={{ valueMissing: t('error.message.empty.email') }}
              />
            </Label>
          )}

          <Label
            className="col-span-6 md:col-span-3"
            description={t('premium.talent.payment.lastName')}
            descriptionClass="font-bold !mb-2 text-ink-medium text-sm"
            required
          >
            <Input
              type="text"
              {...record('lastName')}
              placeholder={t('premium.talent.placeholder.name')}
              minLength={1}
              required
              customValidityMessages={{ valueMissing: t('error.message.empty.lastname') }}
            />
          </Label>


          <Label
            className="col-span-6 md:col-span-3"
            description={t('premium.talent.payment.firstName')}
            descriptionClass="font-bold !mb-2 text-ink-medium text-sm"
            required
          >
            <Input
              type="text"
              {...record('firstName')}
              placeholder={t('premium.talent.placeholder.firstName')}
              minLength={1}
              required
              customValidityMessages={{ valueMissing: t('error.message.empty.firstname') }}
            />
          </Label>

          {/* Job LOCATION autocomplete; must be BOX type ************************************** */}
          <Label
            required
            htmlFor="address"
            description={t('premium.talent.payment.address')}
            className="col-span-6 mb-6"
          >
            <GooglePlacesInput
              record={record}
              recordName="address"
              customValidityMessages={{ valueMissing: t('error.message.t-shirt.address') }}
            />
          </Label>
        </div>

        <hr className="col-span-6 my-3" />

        <div className="mt-3 text-sm font-bold uppercase text-ink-light">{t('payment.card.stripe')}</div>
        <div className="flex justify-between">
          <div className="my-6">
            <CreditCardList className="shrink-0" logoSize="w-6" />
          </div>
          <div className="my-6">
            <ImageBj
              src={poweredByStripe as string}
              alt="Powered by Stripe"
              width={20}
              height={20}
              className="ml-auto h-5 w-auto opacity-60"
            />
          </div>
        </div>
        <div className="mb-6 flex justify-center">
          <Button type="submit" rounding="full" color="secondary">
            { Number(paymentDetails?.price) === 0 ? t('subscription.btn.start_trial') : t('premium.talent.orderNow')}
          </Button>
        </div>
        {paymentDetails?.price === 0 && (
        <>
          <hr className="my-3" />
          <div
            className="mb-6 mt-3 text-sm font-bold uppercase text-ink-light"
          >
            {t('premium_free_trial.faq.head.title')}
          </div>
          <div className="mb-5 rounded-md bg-surface">
            {benefits.map((item, index: number) => (
              <Disclosure
                as="div"
                className="border border-solid border-gray-300 p-2"
                key={item.id}
                defaultOpen={index === 0}
              >
                {({ open }) => (
                  <>
                    <Disclosure.Button
                      className="flex w-full  justify-between p-4 text-left text-lg font-medium text-ink"
                    >
                      <h2 className="font-bold">{item.title}</h2>
                      <ChevronDownIcon
                        className={`${
                          open ? 'rotate-180' : ''
                        } size-6 text-ink`}
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel className="px-4 py-2 text-md text-ink">
                      {item.text}
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}
          </div>
        </>
        )}


      </form>
    </div>
  );
};
